import React, { createContext, useContext } from 'react';
import { Cloudinary } from 'cloudinary-core';
import { constantsCdnServing } from 'ni-constants';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

/**
 * Cloudinary credentials should be defined in ENV
 *
 * @example CLOUDINARY_URL=cloudinary://my_key:my_secret@my_cloud_name
 * @alias https://cloudinary.com/documentation/node_integration#overview
 */

var cloudinary;
var cloudinaryDomains = constantsCdnServing.cloudinaryDomains;
var INSTANCE_CACHE = {};
var defaultCfg = {
  cname: cloudinaryDomains["default"],
  cloud_name: 'dxtwueye6',
  private_cdn: true,
  secure: true,
  secure_distribution: cloudinaryDomains["default"]
};
/**
 * Build full cloudinary image url
 *
 * @param {String} url
 * @param {Object} [transformation={}]
 * @returns
 */

function processImage(url) {
  var transformation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  try {
    var isFetch = transformation.type === 'fetch'; // fetch API allows to use external assets

    var path = isFetch ? url : url.replace(/^(?:\/\/|[^/]+)*\//, '');
    return cloudinary.url(path, _objectSpread2(_objectSpread2({}, getDefaultTransformation(url)), transformation));
  } catch (_unused) {
    return url;
  }
}
/**
 * Returns a default transformation object
 */

function getDefaultTransformation(url) {
  var baseDefaultTransformation = {
    quality: 'auto',
    use_root_path: true
  };
  var ext = url.split('.').pop(); // auto format with svg produces bad resuls

  if (ext === 'svg') {
    return _objectSpread2(_objectSpread2({}, baseDefaultTransformation), {}, {
      sanitaize: true
    });
  }

  return _objectSpread2(_objectSpread2({}, baseDefaultTransformation), {}, {
    fetch_format: 'auto'
  });
}
/**
 *
 *
 * @param {Object} cfg
 * @param {String} cfg.cloud_name
 * @param {String} [cfg.api_key]
 * @param {String} [cfg.api_secret]
 */


function init(cfg) {
  var cname = (cfg === null || cfg === void 0 ? void 0 : cfg.cname) || defaultCfg.cname;

  if (!INSTANCE_CACHE[cname]) {
    INSTANCE_CACHE[cname] = new Cloudinary(_objectSpread2(_objectSpread2({}, defaultCfg), cfg));
  }

  cloudinary = INSTANCE_CACHE[cname];
}

/**
 * process image and it's transformations based on currently defined
 * external CDN image provider
 */
function processTransformations(_ref) {
  var url = _ref.url,
      cloudinaryConfig = _ref.cloudinaryConfig,
      _ref$isCloudinaryEnab = _ref.isCloudinaryEnabled,
      isCloudinaryEnabled = _ref$isCloudinaryEnab === void 0 ? false : _ref$isCloudinaryEnab,
      _ref$cdnDomain = _ref.cdnDomain,
      cdnDomain = _ref$cdnDomain === void 0 ? '' : _ref$cdnDomain,
      _ref$transform = _ref.transform,
      transform = _ref$transform === void 0 ? {} : _ref$transform;

  if (isCloudinaryEnabled) {
    init(cloudinaryConfig); // let's find out if external image, and if so, set transformations to use fetch API

    var externalUrlSettings = {};

    if (!url.includes(cdnDomain)) {
      externalUrlSettings.type = 'fetch';
      externalUrlSettings.use_root_path = false;
    }

    return processImage(url, _objectSpread2(_objectSpread2({}, transform), externalUrlSettings));
  }

  return url;
}

var ASSETS;
/**
 * Constructs CDN link for a given `url`, `name` and `env`..
 *
 * @method cdn
 * @param {String} url original url
 * @param {String} name (data|static) name
 * @param {{data: String, static: String}} assetsCdn assets data object as returned from Rapido
 * @param {String} env environment
 * @return {String} the constructed URL
 */

(function (ASSETS) {
  ASSETS["STATIC"] = "static";
  ASSETS["DATA"] = "data";
})(ASSETS || (ASSETS = {}));

function cdn(url, name, assetsCdn, env) {
  if (!assetsCdn) {
    return url;
  }

  var combinedUrl = '';

  if (url) {
    var cdnPath = assetsCdn[name];

    if (name === 'data') {
      combinedUrl = url.indexOf('http') === 0 || url.indexOf('//') === 0 ? url.replace(/([^:]\/)\/+/g, '$1') : "".concat(cdnPath, "/").concat(env, "/").concat(url).replace(/([^:]\/)\/+/g, '$1');
    } else {
      combinedUrl = url.indexOf('http') === 0 || url.indexOf('//') === 0 ? url.replace(/([^:]\/)\/+/g, '$1') : combinedUrl = "".concat(cdnPath, "/").concat(url).replace(/([^:]\/)\/+/g, '$1');
    }
  }

  return combinedUrl;
}
function cdnStatic(url, assetsCdn, env) {
  return cdn(url, ASSETS.STATIC, assetsCdn, env);
}
function cdnData(url, assetsCdn, env) {
  return cdn(url, ASSETS.DATA, assetsCdn, env);
}
function cdnHost(assetPath) {
  var _URL = new URL("http:".concat(assetPath)),
      host = _URL.host;

  return host;
}
function buildCloudinarySrc(url) {
  var transform = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var cloudinaryConfig = arguments.length > 2 ? arguments[2] : undefined;
  var isCloudinaryEnabled = arguments.length > 3 ? arguments[3] : undefined;
  var cdnDomain = arguments.length > 4 ? arguments[4] : undefined;
  return processTransformations({
    url: url,
    cloudinaryConfig: cloudinaryConfig,
    isCloudinaryEnabled: isCloudinaryEnabled,
    cdnDomain: cdnDomain,
    transform: transform
  });
}

var CdnContext = /*#__PURE__*/createContext({
  cdnData: function cdnData(url) {
    return url;
  },
  cdnStatic: function cdnStatic(url) {
    return url;
  },
  buildCloudnarySrc: function buildCloudnarySrc(src) {
    return src;
  },
  cdnDomain: '',
  isCloudinaryEnabled: false,
  cloudinaryConfig: {},
  assetsCdn: {}
});
var useCdnContext = function useCdnContext() {
  return useContext(CdnContext);
};
var CdnContextProvider = function CdnContextProvider(_ref) {
  var _assetsCdn$cloudinary, _rapido$qs;

  var assetsCdn = _ref.assetsCdn,
      niEnv = _ref.niEnv,
      rapido = _ref.rapido,
      children = _ref.children;
  var cdnContextValue = {
    assetsCdn: assetsCdn,
    cloudinaryConfig: assetsCdn === null || assetsCdn === void 0 ? void 0 : assetsCdn.cloudinary,
    cdnDomain: (assetsCdn === null || assetsCdn === void 0 ? void 0 : assetsCdn.data) || '',
    isCloudinaryEnabled: (assetsCdn === null || assetsCdn === void 0 ? void 0 : (_assetsCdn$cloudinary = assetsCdn.cloudinary) === null || _assetsCdn$cloudinary === void 0 ? void 0 : _assetsCdn$cloudinary.enable) === 'true' || (rapido === null || rapido === void 0 ? void 0 : (_rapido$qs = rapido.qs) === null || _rapido$qs === void 0 ? void 0 : _rapido$qs.cloudinary) === 'true',
    cdnData: function cdnData$1(url) {
      return cdnData(url, assetsCdn, niEnv);
    },
    cdnStatic: function cdnStatic$1(url) {
      return cdnStatic(url, assetsCdn, niEnv);
    },
    buildMediaUrl: function buildMediaUrl(url, transform) {
      return processTransformations({
        url: url,
        transform: transform,
        cloudinaryConfig: cdnContextValue.cloudinaryConfig,
        isCloudinaryEnabled: cdnContextValue.isCloudinaryEnabled,
        cdnDomain: cdnContextValue.cdnDomain
      });
    },
    buildCloudnarySrc: function buildCloudnarySrc(src, transform) {
      return buildCloudinarySrc(src, transform, cdnContextValue.cloudinaryConfig, cdnContextValue.isCloudinaryEnabled, cdnContextValue.cdnDomain);
    }
  };
  return /*#__PURE__*/React.createElement(CdnContext.Provider, {
    value: cdnContextValue
  }, children);
};

export { CdnContext, CdnContextProvider, cdn, cdnData, cdnHost, cdnStatic, init as cloudinaryInit, processImage as cloudinaryProcessImage, processTransformations, useCdnContext };
